import { twMerge } from "tailwind-merge";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import type {
  SupplierDistributionCoverageZoneType,
  SupplierDistributionPreviewZoneType,
  SupplierDistributionPreviewPatternType,
  SupplierDistributionPreviewJourneyType,
} from "@sellar-io/api-v2/types";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  Button,
  H5,
  Icon,
  InputField,
  InputContainer,
  InputSelect,
  InputOption,
  InputText,
  TableSimple,
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
} from "@sellar-io/components";
import { useSupplierDistributionCoverage } from "../../hooks/api/v2/sellarDistributionAvailability/useSupplierDistributionCoverage";
import { useSupplierDistributionPreview } from "../../hooks/api/v2/sellarDistributionAvailability/useSupplierDistributionPreview";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { Delivery } from "./Delivery";
import { Alert } from "../../components/alert/Alert";
import { groupPostcodes } from "./groupPostcodes";
import {
  doPostcodeComponentsOverlap,
  getPostcodeComponentSpecificity,
  normalizePostcodeComponent,
} from "@sellar-io/common";

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(timezone);
const formatDate = (value: string) =>
  dayjs(value).tz("Europe/London").format("ddd D MMM YYYY");
const formatTime = (value: string) =>
  dayjs(value).tz("Europe/London").format("ddd D MMM YYYY, HH:mm [UK] (Z)");
const formatDrawerDate = (value: string) =>
  dayjs(value).tz("Europe/London").format("ddd Do MMM");
const formatDrawerTime = (value: string) =>
  dayjs(value).tz("Europe/London").format("HH:mm [on] ddd Do MMM");
const statusLabels = {
  current: "Current setup",
  theoretical: "Theoretical — not enabled",
  unavailable: "Unavailable",
} satisfies Record<SupplierDistributionPreviewZoneType["status"], string>;
const statusLabel = (zone: SupplierDistributionPreviewZoneType) =>
  statusLabels[zone.status];
const PostcodeSearch = ({
  value,
  onChange,
  onSearch,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  onSearch?: () => void;
  label: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <form
      role="search"
      className="w-60 max-w-full"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch?.();
        inputRef.current?.focus();
      }}
    >
      <InputContainer className="h-9 gap-0 rounded p-0">
        <InputText
          ref={inputRef}
          aria-label={label}
          placeholder="Search by postcode…"
          className="min-w-0 flex-1 px-3 text-base"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            className="flex h-full w-8 shrink-0 items-center justify-center hover:bg-zinc-100"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
          >
            <Icon icon="CLOSE" size={0.75} />
          </button>
        )}
        <button
          type="submit"
          aria-label={label}
          className="flex h-full w-12 shrink-0 items-center justify-center border-l border-zinc-300 bg-zinc-100 hover:bg-zinc-200"
        >
          <Icon icon="MAGNIFY" />
        </button>
      </InputContainer>
    </form>
  );
};
const weekdays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
const formatWeekdays = (days: string[], short = false) =>
  weekdays
    .filter((day) => days.includes(day))
    .map((day) => {
      const label = day.charAt(0).toUpperCase() + day.slice(1);
      return short ? label.slice(0, 3) : label;
    });
const DeliveryPatterns = ({
  patterns,
}: {
  patterns: SupplierDistributionPreviewPatternType[];
}) => (
  <div className="space-y-6">
    {patterns.map((pattern, index) => (
      <div key={index}>
        <p className="mb-3 font-semibold">
          {pattern.weekInterval === 1
            ? "Deliveries weekly on:"
            : `Deliveries every ${pattern.weekInterval} weeks on:`}
        </p>
        {formatWeekdays(pattern.days).map((day) => (
          <p key={day}>{day}</p>
        ))}
        {!pattern.days.length && <p>No delivery days configured.</p>}
        {pattern.weekInterval > 1 && (
          <p>Schedule anchor: {formatDate(pattern.anchor)}</p>
        )}
      </div>
    ))}
    {!patterns.length && <p>No active delivery schedule configured.</p>}
  </div>
);
const Detail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div>
    <dt>{label}</dt>
    <dd className="font-semibold">{children}</dd>
  </div>
);
const Postcodes = ({ postcodes }: { postcodes: string[] }) => (
  <div className="flex flex-wrap gap-x-1">
    {postcodes.map((postcode, index) => (
      <span key={postcode} className="whitespace-nowrap">
        {postcode}
        {index < postcodes.length - 1 ? "," : ""}
      </span>
    ))}
  </div>
);
const Journey = ({
  journey,
  orderPlacedAt,
}: {
  journey: SupplierDistributionPreviewJourneyType;
  orderPlacedAt: string;
}) => (
  <>
    {journey.collection && (
      <Detail label="Your collection day(s)">
        {formatWeekdays(journey.collection.days, true).join(", ")}
      </Detail>
    )}
    <Detail label="For an order placed at">
      <time dateTime={orderPlacedAt} title={formatTime(orderPlacedAt)}>
        {formatDrawerTime(orderPlacedAt)}
      </time>
    </Detail>
    <Detail label="Finalise order latest by">
      <time
        dateTime={journey.buyerOrderCutoffAt}
        title={formatTime(journey.buyerOrderCutoffAt)}
      >
        {formatDrawerTime(journey.buyerOrderCutoffAt)}
      </time>
    </Detail>
    {journey.palletArrivalAt && (
      <Detail label="Pallet arrival at depot">
        {formatDrawerDate(journey.palletArrivalAt)} · {journey.originDepot}
      </Detail>
    )}
    <Detail label="Estimate delivery date">
      {formatDrawerDate(journey.deliveryAt)}
    </Detail>
    <Detail label="Total lead time (calendar days)">
      {journey.calendarDays} days
    </Detail>
  </>
);

export const SupplierDistribution = () => {
  const { business } = useSellarCurrentBusiness();
  const unbundling = useRemoteFeatureFlags().isActive("unbundlingReleaseFinal");
  return unbundling ? (
    <Delivery>
      <DistributionContent key={business?.id} />
    </Delivery>
  ) : (
    <>
      <NavigationBarMenu title="Sellar Distribution" />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <DistributionContent key={business?.id} />
      </div>
    </>
  );
};

const DistributionContent = () => {
  const [view, setView] = useState("postcodes");
  const [coverageSearch, setCoverageSearch] = useState("");
  const [method, setMethod] = useState("");
  const [postcode, setPostcode] = useState("");
  const [depotId, setDepotId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [debouncedPostcode] = useDebounce(
    normalizePostcodeComponent(postcode),
    500,
  );
  const postcodePending =
    normalizePostcodeComponent(postcode) !== debouncedPostcode;
  const validPostcode = ["district", "sector", "unit"].includes(
    getPostcodeComponentSpecificity(debouncedPostcode),
  );
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [modalId, setModalId] = useState<number | null>(null);
  const coverage = useSupplierDistributionCoverage();
  const access = coverage.data?.access;
  const canEstimate = access === "active";
  const selectedZone = method === "area" && zoneId ? Number(zoneId) : undefined;
  const destinationReady =
    method === "postcode" ? validPostcode && !postcodePending : !!selectedZone;
  const query = useSupplierDistributionPreview(
    canEstimate && view === "schedules" && destinationReady
      ? {
          postcode: method === "postcode" ? debouncedPostcode : "",
          zoneId: selectedZone,
          assumptions: "",
        }
      : null,
  );
  const data = destinationReady && !query.isError ? query.data : undefined;
  const zones = data?.zones ?? [];
  const network = coverage.data?.zones ?? [];
  const drawer = zones.find((z) => z.id === drawerId);
  const modal = network.find((z) => z.id === modalId);
  const depots = [
    ...new Map(network.map((z) => [z.depotId, z.depot])).entries(),
  ];
  const postcodeRows = network.flatMap((zone) =>
    groupPostcodes(zone.postcodes)
      .filter(
        (group) =>
          !coverageSearch.trim() ||
          zone.postcodes
            .filter((p) => (p.match(/^[A-Z]+/)?.[0] ?? p) === group.area)
            .some((p) =>
              doPostcodeComponentsOverlap({ left: p, right: coverageSearch }),
            ),
      )
      .map((group) => ({ zone, ...group })),
  );
  const zoneButton = (
    zone:
      | SupplierDistributionCoverageZoneType
      | SupplierDistributionPreviewZoneType,
  ) => (
    <button
      type="button"
      className="text-left underline"
      onClick={() => setModalId(zone.id)}
    >
      {zone.name}
    </button>
  );
  const clearEstimate = () => {
    setDrawerId(null);
  };
  return (
    <div className="space-y-6 text-sm">
      <div>
        <H5>Sellar Distribution</H5>
        <p>
          View postcodes and delivery days covered by Sellar Distribution. You
          can also get a delivery estimate for a specific postcode based on your
          current schedule using the tools below.
        </p>
      </div>
      {coverage.isPending && <p role="status">Loading delivery areas…</p>}
      {coverage.isError && (
        <Alert type="error" message="Delivery areas could not be refreshed.">
          <Button onClick={() => coverage.refetch()}>Try again</Button>
        </Alert>
      )}
      {access && access !== "active" && (
        <Alert
          type="sellar"
          className="w-full"
          title={
            access === "outside_area"
              ? "Sellar Distribution is not yet collecting from your area"
              : "Sellar Distribution collects from your area!"
          }
          message="Use the link below or contact your Sellar account manager for more details about our dray delivery service run by our expert team on our own vans. No MOQs, no delivery charges, no couriers."
        >
          <a
            href="https://sellar.io/distribution"
            target="_blank"
            rel="noreferrer"
            className="self-start rounded border border-zinc-300 bg-white px-3 py-1.5"
          >
            Learn more
          </a>
        </Alert>
      )}
      {access && access !== "outside_area" && (
        <div
          className="inline-flex items-center rounded-lg border border-zinc-300 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
          role="group"
          aria-label="Distribution view"
        >
          {[
            {
              value: "postcodes",
              label: "Delivery areas",
              icon: "MAP_MARKER" as const,
            },
            {
              value: "schedules",
              label: "Delivery date estimates",
              icon: "CALENDAR_OUTLINE" as const,
            },
          ].map((tab, index) => (
            <Button
              key={tab.value}
              disabled={tab.value === "schedules" && !canEstimate}
              color="transparent"
              size="small"
              aria-pressed={view === tab.value}
              className={twMerge(
                "flex items-center gap-1 text-sm font-medium normal-case",
                index === 0
                  ? "rounded-r-none border-r border-solid border-zinc-400"
                  : "rounded-l-none",
                view === tab.value ? "bg-zinc-200" : "bg-white",
              )}
              onClick={() => {
                setView(tab.value);
                setDrawerId(null);
              }}
            >
              <Icon icon={tab.icon} />
              {tab.label}
            </Button>
          ))}
        </div>
      )}
      {(view === "postcodes" || !canEstimate) && coverage.data && (
        <div className="space-y-4">
          <PostcodeSearch
            label="Search delivery postcodes"
            value={coverageSearch}
            onChange={setCoverageSearch}
          />
          <div className="overflow-x-auto">
            <div className="min-w-[650px]">
              <TableSimple
                roundedTop
                data={postcodeRows}
                getRowId={(r) => `${r.zone.id}:${r.area}`}
                gridClasses="grid-cols-[1fr_1fr_1.5fr_3.5fr]"
                noResultsText="No matching SD postcode coverage."
                columns={[
                  {
                    id: "depot",
                    header: "Delivering depot",
                    cell: ({ row }) => row.original.zone.depot,
                  },
                  {
                    id: "zone",
                    header: "Delivery zone",
                    cell: ({ row }) => zoneButton(row.original.zone),
                  },
                  {
                    id: "days",
                    header: "Delivery days",
                    cell: ({ row }) => (
                      <div className="space-y-1">
                        {row.original.zone.patterns.map((p, i) => (
                          <p key={i}>
                            {p.days
                              .map(
                                (day) =>
                                  day[0]!.toUpperCase() + day.slice(1, 3),
                              )
                              .join(", ") || "No days configured"}
                            {p.weekInterval !== 1 &&
                              ` · Every ${p.weekInterval} weeks`}
                          </p>
                        ))}
                      </div>
                    ),
                  },
                  {
                    id: "postcodes",
                    header: "Postcodes",
                    cell: ({ row }) => (
                      <Postcodes postcodes={row.original.postcodes} />
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}
      {view === "schedules" && canEstimate && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <InputField
              label="Find delivery date estimate by"
              className="w-60"
              containerClassName="h-9 rounded-lg px-3 py-1"
            >
              <InputSelect
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value);
                  setPostcode("");
                  setDepotId("");
                  setZoneId("");
                  clearEstimate();
                }}
              >
                <InputOption value="">Choose…</InputOption>
                <InputOption value="postcode">Postcode</InputOption>
                <InputOption value="area">Delivery area</InputOption>
              </InputSelect>
            </InputField>
            {method === "postcode" && (
              <PostcodeSearch
                label="Customer postcode"
                value={postcode}
                onChange={(value) => {
                  setPostcode(value);
                  clearEstimate();
                }}
                onSearch={() => {
                  if (destinationReady && !query.isFetching) {
                    void query.refetch();
                  }
                }}
              />
            )}
            {method === "area" && (
              <>
                <InputField
                  label="Depot"
                  className="w-60"
                  containerClassName="h-9 rounded-lg px-3 py-1"
                >
                  <InputSelect
                    value={depotId}
                    onChange={(e) => {
                      setDepotId(e.target.value);
                      setZoneId("");
                      clearEstimate();
                    }}
                  >
                    <InputOption value="">Choose…</InputOption>
                    {depots.map(([id, name]) => (
                      <InputOption key={id} value={id}>
                        {name}
                      </InputOption>
                    ))}
                  </InputSelect>
                </InputField>
                <InputField
                  label="Delivery area"
                  className="w-60"
                  containerClassName="h-9 rounded-lg px-3 py-1"
                >
                  <InputSelect
                    disabled={!depotId}
                    value={zoneId}
                    onChange={(e) => {
                      setZoneId(e.target.value);
                      clearEstimate();
                    }}
                  >
                    <InputOption value="">Choose…</InputOption>
                    {network
                      .filter((z) => z.depotId === Number(depotId))
                      .map((z) => (
                        <InputOption key={z.id} value={z.id}>
                          {z.name}
                        </InputOption>
                      ))}
                  </InputSelect>
                </InputField>
              </>
            )}
          </div>
          {method === "postcode" &&
            postcode.trim() &&
            !postcodePending &&
            !validPostcode && (
              <p role="status">
                Enter at least a postcode district, such as SK1.
              </p>
            )}
          {destinationReady && query.isPending && (
            <p role="status">Calculating delivery estimates…</p>
          )}
          {destinationReady && query.isError && (
            <Alert
              type="error"
              message="Delivery estimates could not be refreshed."
            >
              <Button onClick={() => query.refetch()}>Try again</Button>
            </Alert>
          )}
          {data && (
            <>
              {query.isFetching && <p role="status">Refreshing…</p>}
              <div className="overflow-x-auto">
                <div className="min-w-[850px]">
                  <TableSimple
                    roundedTop
                    data={zones}
                    getRowId={(z) => z.id}
                    gridClasses="grid-cols-[1fr_1.2fr_1.6fr_1.5fr_0.7fr_0.8fr]"
                    noResultsText="No matching SD postcode coverage."
                    columns={[
                      {
                        id: "depot",
                        header: "Delivering depot",
                        cell: ({ row }) => row.original.depot,
                      },
                      {
                        id: "zone",
                        header: "Delivery zone",
                        cell: ({ row }) => (
                          <div>
                            {zoneButton(row.original)}
                            {row.original.status !== "current" && (
                              <p className="text-xs">
                                {statusLabel(row.original)}
                              </p>
                            )}
                          </div>
                        ),
                      },
                      {
                        id: "order",
                        header: "For an order placed at",
                        cell: () =>
                          dayjs(data.orderPlacedAt)
                            .tz("Europe/London")
                            .format("HH:mm [on] ddd D MMM"),
                      },
                      {
                        id: "delivery",
                        header: "Estimate delivery date by",
                        cell: ({ row }) =>
                          row.original.journey
                            ? formatDate(row.original.journey.deliveryAt)
                            : row.original.reason,
                      },
                      {
                        id: "lead",
                        header: "Lead time",
                        cell: ({ row }) =>
                          row.original.journey
                            ? `${row.original.journey.calendarDays} days`
                            : "Unavailable",
                      },
                      {
                        id: "action",
                        header: "",
                        cell: ({ row }) => (
                          <button
                            type="button"
                            className="underline"
                            onClick={() => setDrawerId(row.original.id)}
                          >
                            View details
                          </button>
                        ),
                      },
                    ]}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <Dialog
        active={modalId !== null}
        onDismiss={() => setModalId(null)}
        className="max-w-[788px] text-base leading-6 text-zinc-800"
      >
        <DialogNavigation
          title={modal ? `Delivery Zone: ${modal.name}` : "Delivery zone"}
          description={
            modal
              ? `Here are the delivery days for ${modal.name} and the postcodes serviced.`
              : undefined
          }
          className="relative gap-4 px-4 pt-5 pb-10"
          titleClassName="text-lg font-semibold"
          descriptionClassName="text-base"
        >
          <button
            type="button"
            aria-label="Close delivery zone"
            className="absolute top-5 left-4 flex h-7 w-7 items-center justify-center"
            onClick={() => setModalId(null)}
          >
            <Icon icon="CHEVRON_DOWN" size={1} />
          </button>
        </DialogNavigation>
        {modal && (
          <DialogContent className="space-y-6 px-4 pb-5">
            <DeliveryPatterns patterns={modal.patterns} />
            <div>
              <p className="mb-3 font-semibold">Delivering to postcodes:</p>
              {groupPostcodes(modal.postcodes).map((group) => (
                <Postcodes key={group.area} postcodes={group.postcodes} />
              ))}
            </div>
          </DialogContent>
        )}
      </Dialog>
      <TabbedDrawer
        active={drawerId !== null}
        setActive={(active) => {
          if (!active) {
            setDrawerId(null);
          }
        }}
        initTab={0}
        panelClassName="w-[480px] text-base leading-6 text-zinc-800"
        containerClassName="md:p-5"
        closeButtonClassName="top-4 right-6 p-0 md:top-9 md:right-11"
        backdropClassName="bg-black/50"
      >
        <TabbedDrawerHeader className="bg-[#F6F6F8]">
          <TabbedDrawerTitle
            title="Delivery details"
            subtitle={`${coverage.data?.originPostcode ?? ""} → ${method === "postcode" ? normalizePostcodeComponent(postcode) : (drawer?.name ?? "")}`}
            className="px-6 pt-10 pb-4 [&>div]:gap-0"
            iconContainerClassName="hidden"
            titleClassName="text-lg leading-7"
            subtitleClassName="text-base leading-6"
          />
        </TabbedDrawerHeader>
        <TabbedDrawerTab tab={0}>
          {query.isError && (
            <p className="p-6" role="alert">
              The journey could not be refreshed.
            </p>
          )}
          {drawer && data && (
            <div className="space-y-4 p-6">
              <dl className="space-y-4">
                <Detail label="Delivery depot">{drawer.depot}</Detail>
                <Detail label="Delivering to zone">
                  {drawer.name} ({drawer.depot})
                </Detail>
                <Detail label="Delivery postcodes">
                  {groupPostcodes(drawer.postcodes).map((g) => (
                    <Postcodes key={g.area} postcodes={g.postcodes} />
                  ))}
                </Detail>
                {drawer.journey && (
                  <Journey
                    journey={drawer.journey}
                    orderPlacedAt={data.orderPlacedAt}
                  />
                )}
              </dl>
              {drawer.status !== "current" && <p>{statusLabel(drawer)}</p>}
              {drawer.reason && <p>{drawer.reason}</p>}
            </div>
          )}
          {drawerId !== null && !drawer && !query.isError && (
            <p role="status" className="p-6">
              Loading delivery details…
            </p>
          )}
        </TabbedDrawerTab>
      </TabbedDrawer>
    </div>
  );
};
